import React, { useState, useEffect } from "react";
import {
  Container,
  Button,
  Row,
  Col,
  Spinner,
  Carousel,
} from "react-bootstrap";
import UpcomingEvents from "../../components/page-components/UpcomingEvents";
import styles from "./EventScreen.module.css";
import "react-lazy-load-image-component/src/effects/blur.css";
import FadeInAnimation from "../../components/animations/FadeInAnimation";

const EventScreen = () => {
  const [events, setEvents] = useState([]); // Auto-loaded from manifest.json
  const [eventsLoading, setEventsLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [viewMode, setViewMode] = useState("mainMenu");
  const [eventImages, setEventImages] = useState([]); // Lazy-loaded images
  const [loading, setLoading] = useState(false); // Track loading state

  // Load the list of past events generated by
  // scripts/generatePastEventsManifest.js (runs automatically before
  // `npm start` / `npm run build`). Adding a new event is now just:
  // drop a new folder in public/img/past-events/ and re-run/build.
  useEffect(() => {
    fetch("/img/past-events/manifest.json")
      .then((res) => res.json())
      .then((data) => setEvents(data))
      .catch((err) =>
        console.error("Failed to load past events manifest:", err)
      )
      .finally(() => setEventsLoading(false));
  }, []);

  const handleEventClick = (event) => {
    setSelectedEvent(event);
    setViewMode("eventDetails");
  };

  const handleReturnToMainMenu = () => {
    setSelectedEvent(null);
    setEventImages([]); // Clear loaded images when returning to main menu
    setViewMode("mainMenu");
  };

  // Lazy-load images for the selected event
  useEffect(() => {
    if (selectedEvent) {
      setLoading(true); // Start loading
      const loadImages = async () => {
        const images = await Promise.all(
          selectedEvent.images.map(
            (image) =>
              new Promise((resolve) => {
                const img = new Image();
                img.src = image;
                img.onload = () => resolve(image);
              })
          )
        );
        setEventImages(images);
        setLoading(false); // Stop loading
      };
      loadImages();
    }
  }, [selectedEvent]);

  const buttonStyle = {
    backgroundColor: "#7e7477ff",
    color: "#ffffff",
    fontFamily: "Roboto Condensed, sans-serif",
    textAlign: "center",
    padding: "10px",
    margin: "auto",
    display: "block",
  };

  return (
    <Container>
      <h1 className="title">Upcoming Events</h1>
      <FadeInAnimation>
        <UpcomingEvents />
      </FadeInAnimation>
      <hr />
      {viewMode === "mainMenu" && (
        <div className="event-images">
          <h1 className="title">Past Events</h1>
          {eventsLoading ? (
            <div className={styles.spinnerContainer}>
              <Spinner animation="grow" variant="secondary">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
            </div>
          ) : (
            <Row className="justify-content-md-center">
              {events.map((event) => (
                <Col sm={12} lg={4} key={event.id}>
                  <div className={styles.eventContainer}>
                    <img
                      src={event.images[0]}
                      alt={event.name}
                      onClick={() => handleEventClick(event)}
                      className={styles.eventImg}
                    />
                    <p className={styles.caption}>
                      Click image to see pictures from
                      <hr />
                      {event.name}
                    </p>
                  </div>
                </Col>
              ))}
            </Row>
          )}
        </div>
      )}
      {viewMode === "eventDetails" && selectedEvent && (
        <div className="event-details">
          <br />
          <h1 className="header">{selectedEvent.name}</h1>
          {loading ? (
            <div className={styles.spinnerContainer}>
              <Spinner animation="grow" variant="secondary">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
            </div>
          ) : (
            <Carousel className={styles.carousel}>
              {eventImages.map((image, index) => (
                <Carousel.Item key={index}>
                  <img
                    src={image}
                    alt={`Event ${selectedEvent.id}`}
                    height="auto"
                    className={styles.carouselImg}
                  />
                </Carousel.Item>
              ))}
            </Carousel>
          )}
          <br />
          <Button style={buttonStyle} onClick={handleReturnToMainMenu}>
            Return to Past Events
          </Button>
          <br />
        </div>
      )}
    </Container>
  );
};

export default EventScreen;
